// Where a counselor starts: the needs the commentary speaks to, and the passages
// under each, linked to the commentary on them. There is no scripture on this
// page — a reference, and the commentary's own words about it.
import type { CounselingTopicWithPassages } from "@bibleunleashed/shared";
import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Header } from "@/components/Header";
import { PageText } from "@/components/PageText";
import { Icon } from "@/components/Icon";
import { useCounselingTopics } from "@/lib/counseling";
import { track } from "@/lib/events";

const fold = (s: string): string => s.toLowerCase().replace(/[‘’]/g, "'");

/** Every word typed appears somewhere in the topic's name, aliases or description. */
function matches(topic: CounselingTopicWithPassages, words: string[]): boolean {
  const text = fold([topic.name, ...topic.aliases, topic.description].join(" "));
  return words.every((w) => text.includes(w));
}

export function Counseling() {
  const { data: topics, isLoading, error } = useCounselingTopics();
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "Counseling · The Bible Unleashed";
  }, []);

  const words = useMemo(() => fold(query).split(/\s+/).filter(Boolean), [query]);
  const shown = useMemo(() => (topics ?? []).filter((t) => matches(t, words)), [topics, words]);

  // A search is counted once the reader stops typing, not at every letter.
  const found = topics ? shown.length : null;
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2 || found === null) return;
    const t = setTimeout(() => track({ type: "search", detail: term, results: found }), 1_200);
    return () => clearTimeout(t);
  }, [query, found]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[760px] px-6 py-16 pb-28">
        <PageText id="counseling" titleClassName="font-serif text-[38px] font-semibold tracking-[-0.015em]" bodyClassName="mt-3 font-serif text-lg" />

        <label className="relative mt-8 block">
          <span className="sr-only">Search the topics</span>
          <Icon name="search" width="16" height="16" className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Anxiety, grief, anger…"
            className="w-full rounded-lg border border-rule bg-paper py-2.5 pr-3 pl-9 font-sans text-[15px] text-ink placeholder:text-ink-3 focus:border-accent focus-visible:outline-none"
          />
        </label>

        {isLoading && <p className="mt-10 font-serif text-ink-3">Loading…</p>}
        {error && <p className="mt-10 font-serif text-ink-2">Couldn’t load the topics: {error.message}</p>}
        {!isLoading && !error && topics?.length === 0 && <p className="mt-10 font-serif text-ink-3">Nothing here yet.</p>}
        {!!topics?.length && shown.length === 0 && (
          <p className="mt-10 font-serif text-ink-2">Nothing matches “{query.trim()}”. Try another word for it.</p>
        )}

        <div className="mt-10 space-y-12">
          {shown.map((topic) => (
            <section key={topic.id} id={topic.id} className="scroll-mt-20">
              <h2 className="font-serif text-[26px] font-semibold tracking-[-0.01em]">{topic.name}</h2>
              {topic.aliases.length > 0 && (
                <p className="mt-0.5 font-sans text-[12px] text-ink-3">Also: {topic.aliases.join(", ")}</p>
              )}
              {topic.description && <p className="mt-2 font-serif text-[17px] leading-relaxed text-ink-2">{topic.description}</p>}
              {topic.passages.length === 0 ? (
                <p className="mt-4 font-sans text-[13px] text-ink-3">No passages tagged yet.</p>
              ) : (
                <ul className="mt-5 space-y-5">
                  {topic.passages.map((p) => (
                    <li key={p.osis} className="border-l-2 border-rule pl-4">
                      <Link href={p.href} className="font-sans text-[15px] font-medium text-accent hover:underline">
                        {p.display}
                      </Link>
                      <span className="font-sans text-[13px] text-ink-3">
                        {" · "}
                        {/* A one-section chapter often repeats its title as the heading. */}
                        {[...new Set([p.chapterTitle, p.heading].filter(Boolean))].join(" — ")}
                      </span>
                      {p.implications && <p className="mt-1.5 font-serif text-[16px] leading-relaxed text-ink-2">{p.implications}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
